import { h } from '@omni-torn/ui';
import {
  attackRefFrom,
  bonusParts,
  cleanLoadoutFilter,
  isWeaponSlot,
  LOADOUT_INTEL_DAYS,
  LOADOUT_SEARCH_MAX,
  LOADOUT_SLOTS,
  loadoutFilterQuery,
  parseFactionQuery,
  RARITIES,
  WEAPON_SLOTS,
  lookupFromApi,
  type AttackFightAnswer,
  type AttackLookup,
  type AttackRef,
  type CatalogRange,
  type LookupHit,
  type LookupWeapon,
  type CatalogWeapon,
  type LoadoutFilter,
  type LoadoutHistoryAnswer,
  type LoadoutSearchAnswer,
  type LoadoutSighting,
  type SharedLoadout,
  type SharedLoadoutItem,
  type WeaponCatalogAnswer,
} from '@omni-torn/shared';
import type { RefItem } from '@omni-torn/shared/torn-ref';
import { navigateTo } from '../nav';
import { server, torn, tornCallsLeft } from '../session';
import { ago, box, ext, facts, kids, loading, money, num, problem, profileUrl, table, tabs, tct } from '../ui';
import { ref } from './torn';

/**
 * Loadouts (website roadmap S17 — SR Tools' Loadout Intel and Weapon Catalog, Torn Intel's enemy loadouts): what players
 * were seen wearing in fights by Omni-Torn users (captured on the attack page they were viewing; terms 3 — every Omni-Torn
 * user; kept a month on the Omni-Torn server, apps/api/src/faction-loadouts-seen.ts).
 *  - /loadouts — search by player (name or id) or faction (its members, from Torn's public list via the server), filtered
 *    by weapon, armor, bonus, slot, rarity and age; the filter is in the address.
 *  - /loadouts/{id} — one player's changes over the month, each item as a card (damage, accuracy, armor, bonuses, rarity);
 *    `?at=` marks one of them. "Share" copies a link to it — the link opens only for someone signed in to Omni-Torn.
 *  - /loadouts?tab=catalog — every weapon seen: how many carry it, damage and accuracy, the bonuses seen on it, Torn's
 *    market value (the items list the server keeps) and a link to its prices on the Market page.
 *  - /loadouts?tab=lookup[&log=] — the attacker lookup (S36, SR Tools'): paste an attack log link, code or a fight's id →
 *    both players, the weapons, ammo and bonuses used, damage per hit and totals. One click = one read of Torn's API attack
 *    log (v2 torn/attacklog, Public) from this browser with your key, within this site's share; if Torn can't answer (or
 *    for an id), the Omni-Torn server's copy (GET /api/loadouts/fight — your own kept fights and the player database's
 *    fights you were in; no Torn call). Never Torn's attack log page itself. Opening a link with `log=` only fills the box.
 * Nothing read here is kept by this page or sent anywhere.
 */

const SHARE_NOTE = 'The link opens only for someone signed in to Omni-Torn.';
const RARITY_WORD: Record<string, string> = { yellow: 'Yellow', orange: 'Orange', red: 'Red' };
const DAY_CHOICES = [1, 3, 7, 14, LOADOUT_INTEL_DAYS];

export async function renderLoadouts(view: HTMLElement, path: string): Promise<void> {
  const id = Number(/^\/loadouts\/(\d+)\/?$/.exec(path)?.[1] ?? 0) || null;
  const q = new URLSearchParams(location.search);
  const tab = id ? 'player' : q.get('tab') === 'catalog' ? 'catalog' : q.get('tab') === 'lookup' ? 'lookup' : 'search';
  const body = h('div', {});
  const bar = tabs(
    [
      { id: 'search', label: 'Search' },
      { id: 'catalog', label: 'Weapon catalog' },
      { id: 'lookup', label: 'Attack lookup' },
    ],
    tab === 'player' ? 'search' : tab,
    (t) => navigateTo(t === 'search' ? '/loadouts' : `/loadouts?tab=${t}`),
  );
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Loadouts'), h('p', { class: 'muted' }, 'What players were seen wearing in fights by Omni-Torn users, in the last month.')),
    bar,
    body,
  );
  if (id) await playerView(body, id, Number(q.get('at')) || null);
  else if (tab === 'catalog') await catalogView(body, q);
  else if (tab === 'lookup') lookupView(body, q);
  else await searchView(body, q);
}

// ---------------------------------------------------------------- item cards

const statLine = (i: SharedLoadoutItem): string =>
  [i.damage != null ? `${num(i.damage, 2)} dmg` : '', i.accuracy != null ? `${num(i.accuracy, 2)} acc` : '', i.armor != null ? `${num(i.armor, 2)} armor` : ''].filter(Boolean).join(' · ');

/** One item: name (its prices on the Market page), slot, damage/accuracy/armor, bonuses with their numbers, rarity. */
function itemCard(i: SharedLoadoutItem, changed = false): HTMLElement {
  return h(
    'div',
    { class: `loadout-card${i.rarity ? ` rarity-${i.rarity}` : ''}${changed ? ' changed' : ''}`, 'data-item': String(i.itemId), 'data-slot': i.slot },
    h('div', { class: 'loadout-card-slot muted' }, i.slot, changed ? h('span', { class: 'badge' }, 'new') : null),
    h('a', { class: 'loadout-card-name', href: `/market/${i.itemId}`, 'data-link': true, title: 'Its prices (Market)' }, i.name),
    statLine(i) ? h('div', { class: 'loadout-card-stats' }, statLine(i)) : null,
    i.bonuses?.length
      ? h(
          'ul',
          { class: 'loadout-card-bonuses' },
          ...i.bonuses.map((b) => {
            const p = bonusParts(b);
            return h('li', {}, p.name, p.value !== null ? h('strong', {}, ` ${num(p.value, 2)}${p.unit === '%' ? '%' : p.unit ? ` ${p.unit}` : ''}`) : null);
          }),
        )
      : null,
    i.rarity ? h('div', { class: 'loadout-card-rarity' }, RARITY_WORD[i.rarity]) : null,
  );
}

const ordered = (l: SharedLoadout) => [...l.items].sort((a, b) => LOADOUT_SLOTS.indexOf(a.slot) - LOADOUT_SLOTS.indexOf(b.slot));

function loadoutCards(l: SharedLoadout, changed: readonly string[] = []): HTMLElement {
  return h('div', { class: 'loadout-cards' }, ...ordered(l).map((i) => itemCard(i, changed.includes(i.slot))));
}

/** Copies a link to the clipboard (or shows it to copy by hand). */
function shareButton(path: string, label = 'Share'): HTMLElement {
  const status = h('span', { class: 'muted share-status', role: 'status' });
  const btn = h('button', { type: 'button', class: 'btn-small', 'data-share': path }, label);
  btn.addEventListener('click', () => {
    const link = `${location.origin}${path}`;
    const shown = () => status.replaceChildren(h('input', { type: 'text', readonly: true, value: link, class: 'share-link', 'aria-label': 'Link to copy' }), ` ${SHARE_NOTE}`);
    if (!navigator.clipboard) return shown();
    navigator.clipboard.writeText(link).then(
      () => (status.textContent = `Link copied. ${SHARE_NOTE}`),
      () => shown(),
    );
  });
  return h('span', { class: 'share' }, btn, status);
}

// ---------------------------------------------------------------- search

/** The page's address for a search (empty fields left out). */
function searchPath(who: string, faction: string, f: LoadoutFilter): string {
  const q = new URLSearchParams({ ...(who ? { who } : {}), ...(faction ? { faction } : {}), ...loadoutFilterQuery(f) });
  const s = q.toString();
  return `/loadouts${s ? `?${s}` : ''}`;
}

function select(name: string, label: string, choices: Array<[string, string]>, value: string): HTMLSelectElement {
  return h('select', { name, 'aria-label': label, 'data-filter': name }, ...choices.map(([v, t]) => h('option', { value: v, ...(v === value ? { selected: true } : {}) }, t))) as HTMLSelectElement;
}

async function searchView(body: HTMLElement, q: URLSearchParams): Promise<void> {
  const filter = cleanLoadoutFilter((k) => q.get(k));
  const who = (q.get('who') ?? '').trim().slice(0, 40);
  const faction = (q.get('faction') ?? '').trim().slice(0, 120);
  const field = (name: string, placeholder: string, value: string) => h('input', { name, type: 'search', placeholder, value, maxlength: '120', autocomplete: 'off', 'aria-label': placeholder, class: 'filter', 'data-filter': name }) as HTMLInputElement;
  const whoIn = field('who', 'Player name or id…', who);
  const facIn = field('faction', 'Faction id or link…', faction);
  const weapon = field('weapon', 'Weapon…', filter.weapon ?? '');
  const armor = field('armor', 'Armor…', filter.armor ?? '');
  const bonus = field('bonus', 'Bonus (Expose, Bleed…)', filter.bonus ?? '');
  const slot = select('slot', 'Slot', [['', 'Any slot'], ...LOADOUT_SLOTS.map((s): [string, string] => [s, s])], filter.slot ?? '');
  const rarity = select('rarity', 'Rarity', [['', 'Any rarity'], ...RARITIES.map((r): [string, string] => [r, `${RARITY_WORD[r]} or better`])], filter.rarity ?? '');
  const days = select('days', 'Seen within', DAY_CHOICES.map((d): [string, string] => [d === LOADOUT_INTEL_DAYS ? '' : String(d), d === LOADOUT_INTEL_DAYS ? 'The last month' : `The last ${d === 1 ? 'day' : `${d} days`}`]), filter.days ? String(filter.days) : '');
  const form = h(
    'form',
    { class: 'loadout-search', 'data-loadouts': 'search', role: 'search', autocomplete: 'off' },
    h('div', { class: 'toolbar' }, whoIn, facIn),
    h('div', { class: 'toolbar' }, weapon, armor, bonus),
    h('div', { class: 'toolbar' }, slot, rarity, days, h('button', { type: 'submit', class: 'btn-primary' }, 'Search'), h('a', { href: '/loadouts', 'data-link': true }, 'Clear')),
  );
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const f = cleanLoadoutFilter((k) => ({ weapon: weapon.value, armor: armor.value, bonus: bonus.value, slot: slot.value, rarity: rarity.value, days: days.value })[k] ?? null);
    navigateTo(searchPath(whoIn.value.trim(), facIn.value.trim(), f));
  });
  const results = h('div', {}, loading('Searching…'));
  body.replaceChildren(box('Find loadouts', [form, h('p', { class: 'muted small' }, 'Leave everything empty for the newest sightings. A faction search reads its members from Torn (the Omni-Torn server, kept an hour).')], { id: 'loadouts-search' }), results);

  // Who: a player id (or "name [id]", a profile link) or a name's start; a faction: its id or link.
  const params = new URLSearchParams(loadoutFilterQuery(filter));
  let problemText = '';
  if (faction) {
    const f = parseFactionQuery(faction);
    if (f && 'id' in f) params.set('faction', String(f.id));
    else problemText = 'Give the faction’s id or its Torn link.';
  } else if (who) {
    const id = /^\[?(\d{1,9})\]?$/.exec(who)?.[1] ?? /\[(\d{1,9})\]\s*$/.exec(who)?.[1] ?? /XID=(\d{1,9})/i.exec(who)?.[1];
    if (id) params.set('player', id);
    else if (/^[\w-]{1,30}$/.test(who)) params.set('name', who);
    else problemText = 'A Torn name has only letters, digits, _ and -.';
  }
  if (problemText) {
    results.replaceChildren(box('Results', h('p', { class: 'form-status bad' }, problemText), { id: 'loadouts-results' }));
    return;
  }
  let r: LoadoutSearchAnswer;
  try {
    r = await server<LoadoutSearchAnswer>('GET', `/api/loadouts/search?${params}`);
  } catch (err) {
    results.replaceChildren(box('Results', problem(err), { id: 'loadouts-results' }));
    return;
  }
  const title = r.faction ? `${r.faction.name} — ${r.rows.length} of ${r.faction.members} members seen` : `${r.rows.length}${r.more ? '+' : ''} ${r.rows.length === 1 ? 'player' : 'players'}`;
  const note = r.faction ? h('p', { class: 'muted small' }, `Members from Torn’s public list, read ${ago(r.faction.at)}${r.faction.calls ? '' : ' (kept)'}. `, h('a', { href: `/factions/${r.faction.id}`, 'data-link': true }, 'Faction profile')) : null;
  const more = r.more ? h('p', { class: 'muted small' }, `Showing the newest ${LOADOUT_SEARCH_MAX}: narrow the search for the rest.`) : null;
  results.replaceChildren(box(title, kids(note, sightingsTable(r.rows, filter), more), { id: 'loadouts-results', extra: shareButton(location.pathname + location.search, 'Share this search') }));
}

const itemsIn = (l: SharedLoadout, weapons: boolean) => ordered(l).filter((i) => isWeaponSlot(i.slot) === weapons);

function itemLine(i: SharedLoadoutItem, f: LoadoutFilter): HTMLElement {
  const hit = (f.weapon && isWeaponSlot(i.slot) && i.name.toLowerCase().includes(f.weapon.toLowerCase())) || (f.armor && !isWeaponSlot(i.slot) && i.name.toLowerCase().includes(f.armor.toLowerCase()));
  const nums = statLine(i);
  return h('div', { class: `loadout-line${hit ? ' hit' : ''}` }, h('span', { class: i.rarity ? `rarity-text-${i.rarity}` : '' }, i.name), nums ? h('span', { class: 'muted' }, ` ${nums}`) : null, i.bonuses?.length ? h('span', { class: 'loadout-bonus' }, ` ${i.bonuses.join(', ')}`) : null);
}

function sightingsTable(rows: readonly LoadoutSighting[], f: LoadoutFilter): HTMLElement {
  return table(
    rows,
    [
      { label: 'Player', get: (r) => h('a', { href: `/loadouts/${r.id}`, 'data-link': true, 'data-player': String(r.id) }, r.name || `Player ${r.id}`, h('span', { class: 'muted' }, ` [${r.id}]`)), sort: (r) => (r.name || String(r.id)).toLowerCase() },
      { label: 'Seen', get: (r) => h('span', { title: tct(r.at) }, ago(r.at)), sort: (r) => r.at },
      { label: 'Weapons', get: (r) => h('div', {}, ...itemsIn(r.loadout, true).map((i) => itemLine(i, f))) },
      { label: 'Armor', get: (r) => h('div', {}, ...itemsIn(r.loadout, false).map((i) => itemLine(i, f))) },
    ],
    { initial: 1, desc: true, empty: 'Nobody seen with that in the last month (a loadout shows once an Omni-Torn user fights them).' },
  );
}

// ---------------------------------------------------------------- one player's changes

async function playerView(body: HTMLElement, id: number, at: number | null): Promise<void> {
  body.replaceChildren(loading('Reading what they were seen wearing…'));
  let r: LoadoutHistoryAnswer;
  try {
    r = await server<LoadoutHistoryAnswer>('GET', `/api/loadouts/player/${id}`);
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  const name = r.name || `Player ${id}`;
  const links = h('p', { class: 'loadout-links' }, h('a', { href: `/lookup/${id}`, 'data-link': true }, 'Player lookup'), ' · ', ext(profileUrl(id), 'Torn profile'), ' · ', h('a', { href: '/loadouts', 'data-link': true }, 'Back to search'));
  if (!r.changes.length) {
    body.replaceChildren(box(`${name} [${id}]`, [links, h('p', { class: 'muted' }, 'No Omni-Torn user has seen what they wear in the last month (it shows once someone fights them).')], { id: 'loadouts-player' }));
    return;
  }
  const marked = at ? r.changes.find((c) => c.from <= at && at <= c.to) ?? r.changes.find((c) => c.from === at) : null;
  const parts = r.changes.map((c, i) => {
    const when = c.from === c.to ? `seen ${ago(c.to)}` : `seen from ${ago(c.from)} to ${ago(c.to)}`;
    const what = i === r.changes.length - 1 ? 'the oldest this month' : c.changed.length ? `changed: ${c.changed.join(', ')}` : 'the same items, other numbers';
    return h(
      'div',
      { class: `loadout-change${c === marked ? ' marked' : ''}`, 'data-change': String(c.from) },
      h('div', { class: 'loadout-change-head' }, h('strong', {}, i === 0 ? 'Newest' : `Before (${i})`), h('span', { class: 'muted' }, ` · ${when} · ${c.seen === 1 ? 'one fight' : `${num(c.seen)} fights`} · ${what}`), shareButton(`/loadouts/${id}?at=${c.from}`)),
      loadoutCards(c.loadout, i === r.changes.length - 1 ? [] : c.changed),
    );
  });
  const summary = facts([
    ['Seen last', h('span', { title: tct(r.changes[0]!.to) }, ago(r.changes[0]!.to))],
    ['Loadouts this month', String(r.changes.length)],
  ]);
  body.replaceChildren(box(`${name} [${id}]`, [links, summary, ...parts], { id: 'loadouts-player', extra: shareButton(`/loadouts/${id}`) }));
  if (marked) body.querySelector('.loadout-change.marked')?.scrollIntoView({ block: 'center' });
}

// ---------------------------------------------------------------- weapon catalog

const rangeText = (r: CatalogRange | null): string => (!r ? '—' : r.min === r.max ? num(r.min, 2) : `${num(r.min, 2)}–${num(r.max, 2)} (avg ${num(r.avg, 2)})`);

function bonusList(w: CatalogWeapon): HTMLElement | string {
  if (!w.bonuses.length) return '—';
  return h(
    'span',
    {},
    ...w.bonuses.slice(0, 6).flatMap((b, i) => {
      const spread = b.min === null ? '' : b.min === b.max ? ` ${num(b.min, 2)}${b.unit === '%' ? '%' : ''}` : ` ${num(b.min, 2)}–${num(b.max!, 2)}${b.unit === '%' ? '%' : ''}`;
      return [i ? ', ' : '', h('a', { href: `/loadouts?weapon=${encodeURIComponent(w.name)}&bonus=${encodeURIComponent(b.name)}`, 'data-link': true }, b.name), `${spread} ×${b.count}`];
    }),
    w.bonuses.length > 6 ? ` +${w.bonuses.length - 6} more` : '',
  );
}

async function catalogView(body: HTMLElement, q: URLSearchParams): Promise<void> {
  body.replaceChildren(loading('Counting every weapon seen…'));
  let cat: WeaponCatalogAnswer;
  let items: Map<number, RefItem> = new Map();
  try {
    const [c, list] = await Promise.all([server<WeaponCatalogAnswer>('GET', '/api/loadouts/catalog'), ref('items').catch(() => null)]);
    cat = c;
    if (list) items = new Map(list.data.map((i) => [i.id, i]));
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  const slotNow = WEAPON_SLOTS.includes(q.get('slot') as never) ? (q.get('slot') as string) : '';
  const slot = select('slot', 'Slot', [['', 'Every weapon'], ...WEAPON_SLOTS.map((s): [string, string] => [s, s])], slotNow);
  const find = h('input', { type: 'search', placeholder: 'Weapon or bonus…', class: 'filter', 'aria-label': 'Weapon or bonus', 'data-filter': 'catalog-q', value: q.get('q') ?? '' }) as HTMLInputElement;
  const holder = h('div', {});
  const draw = () => {
    const s = find.value.trim().toLowerCase();
    const list = cat.weapons.filter((w) => (!slot.value || w.slot === slot.value) && (!s || w.name.toLowerCase().includes(s) || w.bonuses.some((b) => b.name.toLowerCase().includes(s))));
    holder.replaceChildren(
      table(
        list,
        [
          { label: 'Weapon', get: (w) => h('a', { href: `/loadouts?weapon=${encodeURIComponent(w.name)}`, 'data-link': true, 'data-weapon': String(w.itemId), title: 'Who was seen with it' }, w.name), sort: (w) => w.name.toLowerCase() },
          { label: 'Slot', get: (w) => w.slot, sort: (w) => WEAPON_SLOTS.indexOf(w.slot) },
          { label: 'Players', get: (w) => num(w.players), sort: (w) => w.players, cls: 'num' },
          { label: 'Damage', get: (w) => rangeText(w.damage), sort: (w) => w.damage?.max ?? -1 },
          { label: 'Accuracy', get: (w) => rangeText(w.accuracy), sort: (w) => w.accuracy?.max ?? -1 },
          { label: 'Bonuses seen', get: (w) => bonusList(w), sort: (w) => w.bonuses.length },
          { label: 'Rarity', get: (w) => [w.rarity.red ? `${w.rarity.red} red` : '', w.rarity.orange ? `${w.rarity.orange} orange` : '', w.rarity.yellow ? `${w.rarity.yellow} yellow` : ''].filter(Boolean).join(', ') || '—', sort: (w) => w.rarity.red * 1e6 + w.rarity.orange * 1e3 + w.rarity.yellow },
          { label: 'Market value', get: (w) => h('a', { href: `/market/${w.itemId}`, 'data-link': true, title: 'Its listings (Market)' }, items.get(w.itemId)?.marketValue ? money(items.get(w.itemId)!.marketValue) : 'Prices'), sort: (w) => items.get(w.itemId)?.marketValue ?? -1, cls: 'num' },
        ],
        { initial: 2, desc: true, empty: cat.weapons.length ? 'No weapon matches.' : 'No weapon seen yet this month (they show once Omni-Torn users fight people).' },
      ),
    );
    const p = new URLSearchParams({ tab: 'catalog', ...(slot.value ? { slot: slot.value } : {}), ...(s ? { q: find.value.trim() } : {}) });
    history.replaceState(null, '', `/loadouts?${p}`);
  };
  slot.addEventListener('change', draw);
  find.addEventListener('input', draw);
  body.replaceChildren(
    box(
      'Weapon catalog',
      [
        h('p', { class: 'muted small' }, `Every weapon in the newest loadout of ${num(cat.players)} ${cat.players === 1 ? 'player' : 'players'} Omni-Torn users saw in fights this month — counted ${ago(cat.at)}. Market value is Torn’s; the link shows the listings.`),
        h('div', { class: 'toolbar' }, slot, find),
        holder,
      ],
      { id: 'loadouts-catalog' },
    ),
  );
  draw();
}

// ---------------------------------------------------------------- attack lookup (S36)

const SOURCE_WORD: Record<string, string> = { api: 'Torn’s API attack log (your key)', yours: 'your own fights (your key, kept by background tracking)', db: 'the Omni-Torn player database (a fight you were in)' };
const KIND_WORD: Record<string, string> = { hit: 'Hit', crit: 'Critical', miss: 'Miss', other: '' };

function lookupView(body: HTMLElement, q: URLSearchParams): void {
  const input = h('input', { type: 'search', name: 'log', placeholder: 'Attack log link, code or fight id…', value: (q.get('log') ?? '').slice(0, 200), maxlength: '400', autocomplete: 'off', 'aria-label': 'Attack log link, code or fight id', class: 'filter', 'data-filter': 'log' }) as HTMLInputElement;
  const go = h('button', { type: 'submit', class: 'btn-primary' }, 'Look up') as HTMLButtonElement;
  const form = h('form', { class: 'loadout-search', 'data-loadouts': 'lookup', role: 'search', autocomplete: 'off' }, h('div', { class: 'toolbar' }, input, go));
  const status = h('p', { class: 'muted small', role: 'status' });
  const results = h('div', {});
  const note = () => status.replaceChildren(`One click reads that fight’s attack log once from Torn’s API with your key (this site’s share: ${tornCallsLeft()} left this minute). If Torn can’t answer, or you give a fight’s id, Omni-Torn shows the fights it already holds that you were in.`);
  note();
  body.replaceChildren(box('Attack lookup', [form, status, h('p', { class: 'muted small' }, 'Paste the link from a fight’s “view” in your events or attacks, or its code. Omni-Torn never opens Torn’s attack log page itself.')], { id: 'loadouts-lookup' }), results);
  let busy = false;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (busy || !e.isTrusted) return;
    const ref = attackRefFrom(input.value);
    if (!ref) {
      results.replaceChildren(box('Fight', h('p', { class: 'form-status bad' }, 'That isn’t an attack log link, code or fight id.'), { id: 'loadouts-fight' }));
      return;
    }
    history.replaceState(null, '', `/loadouts?tab=lookup&log=${encodeURIComponent('code' in ref ? ref.code : String(ref.id))}`);
    busy = true;
    go.disabled = true;
    void lookUp(results, ref).finally(() => {
      busy = false;
      go.disabled = false;
      note();
    });
  });
}

/** One look-up: Torn's API attack log by code (one read), else the server's copy. */
async function lookUp(results: HTMLElement, ref: AttackRef): Promise<void> {
  results.replaceChildren(loading('Reading the fight…'));
  let tornNote = '';
  if ('code' in ref) {
    try {
      const fight = lookupFromApi(await torn('/v2/torn/attacklog', { log: ref.code }, 10 * 60_000), ref.code);
      if (fight) return void results.replaceChildren(fightView(fight));
      tornNote = 'Torn’s API has no lines for that attack log.';
    } catch (err) {
      tornNote = `Torn’s API didn’t give that attack log: ${err instanceof Error ? err.message : 'it refused.'}`;
    }
  }
  let r: AttackFightAnswer;
  try {
    r = await server<AttackFightAnswer>('GET', `/api/loadouts/fight?${'code' in ref ? `code=${encodeURIComponent(ref.code)}` : `id=${ref.id}`}`);
  } catch (err) {
    return void results.replaceChildren(box('Fight', kids(tornNote ? h('p', { class: 'muted' }, tornNote) : null, problem(err)), { id: 'loadouts-fight' }));
  }
  if (!r.fight) {
    const none = 'Omni-Torn holds no fight of yours with that ' + ('code' in ref ? 'code' : 'id') + ' (it can show only fights you were in: your own, or ones an Omni-Torn user sent from a fight with you).';
    return void results.replaceChildren(box('Fight', kids(tornNote ? h('p', { class: 'muted', 'data-torn-note': true }, tornNote) : null, h('p', { class: 'muted' }, none)), { id: 'loadouts-fight' }));
  }
  results.replaceChildren(fightView(r.fight, tornNote));
}

const who = (p: AttackLookup['attacker']): HTMLElement | string =>
  !p ? 'Unknown' : p.id ? h('span', {}, h('a', { href: `/loadouts/${p.id}`, 'data-link': true, title: 'What they were seen wearing', 'data-player': String(p.id) }, p.name), h('span', { class: 'muted' }, ` [${p.id}] `), ext(profileUrl(p.id), 'Torn')) : p.name;

function fightView(f: AttackLookup, tornNote = ''): HTMLElement {
  const sideName = (s: LookupHit['side']) => (s === 'attacker' ? f.attacker?.name ?? 'Attacker' : s === 'defender' ? f.defender?.name ?? 'Defender' : '—');
  const when = f.started ? h('span', { title: tct(f.started * 1000) }, ago(f.started * 1000)) : '—';
  const head = facts([
    ['Attacker', who(f.attacker)],
    ['Defender', who(f.defender)],
    ['Result', f.result ?? '—'],
    ['When', when],
    ['From', f.sources.map((s) => SOURCE_WORD[s] ?? s).join('; ')],
  ]);
  const totals = table(
    (['attacker', 'defender'] as const).map((s) => ({ s, t: f.totals[s] })),
    [
      { label: 'Player', get: (r) => sideName(r.s) },
      { label: 'Hits', get: (r) => num(r.t.hits), cls: 'num' },
      { label: 'Criticals', get: (r) => num(r.t.crits), cls: 'num' },
      { label: 'Misses', get: (r) => num(r.t.misses), cls: 'num' },
      { label: 'Damage', get: (r) => num(r.t.damage), cls: 'num' },
    ],
    { empty: '' },
  );
  const weapons = table(
    f.weapons,
    [
      { label: 'Player', get: (w: LookupWeapon) => sideName(w.side), sort: (w) => (w.side === 'attacker' ? 0 : 1) },
      { label: 'Weapon', get: (w) => h('a', { href: `/loadouts?weapon=${encodeURIComponent(w.name)}`, 'data-link': true, title: 'Who was seen with it' }, w.name), sort: (w) => w.name.toLowerCase() },
      { label: 'Ammo', get: (w) => w.ammo.join(', ') || '—' },
      { label: 'Bonuses', get: (w) => (w.bonuses.length ? h('span', { class: 'loadout-bonus' }, w.bonuses.join(', ')) : '—') },
      { label: 'Hits', get: (w) => `${num(w.hits)}${w.crits ? ` (${num(w.crits)} crit)` : ''}`, sort: (w) => w.hits, cls: 'num' },
      { label: 'Misses', get: (w) => num(w.misses), sort: (w) => w.misses, cls: 'num' },
      { label: 'Damage', get: (w) => num(w.damage), sort: (w) => w.damage, cls: 'num' },
    ],
    { initial: 6, desc: true, empty: 'No weapon named in this fight.' },
  );
  const hits = f.hits.filter((x) => x.kind !== 'other' || x.bonuses.length);
  const turns = f.hits.length
    ? table(
        hits.map((x, i) => ({ ...x, n: i + 1 })),
        [
          { label: '#', get: (x) => String(x.n), sort: (x) => x.n, cls: 'num' },
          { label: 'Who', get: (x) => sideName(x.side) },
          { label: 'What', get: (x) => h('span', { class: `fight-kind fight-${x.kind}` }, KIND_WORD[x.kind] || '—') },
          { label: 'Weapon', get: (x) => h('span', {}, ...kids(x.weapon ?? '—', x.ammo ? h('span', { class: 'muted' }, ` · ${x.ammo}`) : null, x.rounds ? h('span', { class: 'muted' }, ` · ${x.rounds} ${x.rounds === 1 ? 'round' : 'rounds'}`) : null)) },
          { label: 'Where', get: (x) => x.zone ?? '—' },
          { label: 'Damage', get: (x) => (x.damage !== null ? num(x.damage) : '—'), sort: (x) => x.damage ?? -1, cls: 'num' },
          { label: 'Bonuses', get: (x) => (x.bonuses.length ? h('span', { class: 'loadout-bonus' }, x.bonuses.join(', ')) : '—') },
          { label: 'Line', get: (x) => h('span', { class: 'muted fight-line' }, x.text) },
        ],
        { initial: 0, desc: false, empty: 'No hits or misses in this fight.' },
      )
    : h('p', { class: 'muted' }, 'This source has no hits (your fights list only says who, when and how it ended). Torn’s API attack log gives every hit: look it up by its code.');
  const again = !f.hits.length && f.code ? h('p', {}, h('a', { href: `/loadouts?tab=lookup&log=${encodeURIComponent(f.code)}`, 'data-link': true, 'data-code': f.code }, 'Fill in its code to read every hit from Torn’s API')) : null;
  return box(
    `${f.attacker?.name ?? 'Someone'} vs ${f.defender?.name ?? 'someone'}`,
    kids(tornNote ? h('p', { class: 'muted', 'data-torn-note': true }, tornNote) : null, head, h('h3', {}, 'Totals'), totals, h('h3', {}, 'Weapons'), weapons, h('h3', {}, 'Hits'), turns, again),
    { id: 'loadouts-fight' },
  );
}
